import { convertToInputDate } from '@/utils/timeFormat';
import { Maybe } from 'monet';

const EMPTY_INPUT_ERRORS = {};

/**
 * @description Примесь создания записи на прием.
 * @type {Vue}
 */
export default {
  computed: {
    /**
     * @descriptions Рекламные каналы.
     * @returns {AdvertisingChannel[]}
     */
    advertisingChannels({ advertisingChannelList, fetchAdvertisingChannelList }) {
      return advertisingChannelList.orLazy(fetchAdvertisingChannelList);
    },
    /**
     * @descriptions Причины отказа.
     * @returns {Object[]}
     */
    rejectionReasons({ rejectionReasonList, fetchRejectionReasonList }) {
      return rejectionReasonList.orLazy(fetchRejectionReasonList);
    },
    /**
     * Уведомление
     *
     * @returns {object}
     */
    notification() {
      return this.waitingPatient;
    },
    /**
     * @description Рабочие дни сотрудника.
     * @returns {string[]}
     */
    appointmentEmployeeWorkDays() {
      const isoDate = convertToInputDate(this.appointmentDate);
      if (!this.appointmentEmployeeWorkDayList.includes(isoDate)) {
        return [...this.appointmentEmployeeWorkDayList, isoDate];
      }
      return this.appointmentEmployeeWorkDayList;
    },
    /**
     * @description Данные записи на прием.
     * @returns {Object}
     */
    appointmentProps() {
      return this.selectedAppointment.fold({})((appointment) => ({
        actions: appointment.actions,
        aggregatorName: appointment.aggregatorName,
        appointmentId: appointment.id,
        appointmentStatus: appointment.status,
        initialAdvertisingChannelId: appointment.advertisingChannel?.id,
        initialAdvertisingChannelName: appointment.advertisingChannel?.name,
        initialDateTime: appointment.dateTime || Date.now(),
        initialIsInitial: appointment.isInitial,
        initialIsOutdated: appointment.isOutdated,
        initialNeedsCreatingPatient: appointment.needsCreatingPatient,
        initialNote: appointment.note,
        initialPatientId: appointment.patientId,
        initialPatientLabels: appointment.patientLabels,
        initialPatientName: appointment.name,
        initialPatientPatronymic: appointment.patronymic,
        initialPatientPhone: appointment.phone,
        initialPatientSurname: appointment.surname,
        initialPatientFullName: appointment.patientFullName,
        initialServices: appointment.services,
        initialTimeEnds: appointment.timeEnds,
        initialTimeStarts: appointment.timeStarts,
        visitId: appointment.visitId,
        waitingIds: appointment.waitingsIds,
        waitingId: appointment.waitingId,
      }));
    },
    /**
     * @description Показывать форму записи на прием.
     * @returns {boolean}
     */
    isShowAppointmentForm() {
      return this.selectedAppointment.isSome();
    },
  },
  data() {
    return {
      /**
       * @description Список рекламных каналов.
       * @type {Maybe<AdvertisingChannel[]>}
       */
      advertisingChannelList: Maybe.None(),
      appointmentDate: new Date(),
      appointmentEmployee: Maybe.none(),
      appointmentEmployeeId: null,
      appointmentEmployeeTimeline: [],
      appointmentEmployeeWorkDayList: [],
      appointmentInputErrors: EMPTY_INPUT_ERRORS,
      appointmentMatchedPatients: [],
      appointmentNotification: [],
      appointmentMonth: null,
      appointmentPatientId: this.$route.params.patientId || null,
      appointmentCrmDealId: this.$route.params.dealId || null,
      appointmentTimeEnds: '',
      waitingPatient: null,
      appointmentTimeStarts: '',
      appointmentWorkEmployees: [],
      selectedAppointment: Maybe.none(),
      appointmentCabinetId: null,
      isAppointmentAwaitRequest: false,
      rejectionReasonList: Maybe.None(),
    };
  },
  methods: {
    /**
     * @description Получить список рекламных каналов.
     * @returns {void}
     */
    fetchAdvertisingChannelList() {
      this.$advertisingChannelGateway.viewList().then((either) => {
        this.advertisingChannelList = Maybe.of(
          either.fold(
            () => [],
            ({ list }) => list,
          ),
        );
      });
      return [];
    },
    /**
     * @description Получить список рекламных каналов.
     * @returns {void}
     */
    fetchRejectionReasonList() {
      this.$crmGateway.viewRejectionReasonList().then((either) => {
        this.rejectionReasonList = Maybe.of(
          either.fold(
            () => [],
            ({ list }) => list,
          ),
        );
      });
      return [];
    },
    /**
     * @description Получить временную шкалу сотрудника на день.
     * @param {Number} employeeId Идентификатор сотрудника.
     * @param {string} isoDate    Дата.
     * @param {string} timeStarts Время начала.
     * @returns {Promise<{actuallyPeriods:Array,timeline:Array}>}
     */
    fetchEmployeeDayTimeline(employeeId, isoDate, timeStarts) {
      const onFail = (error) => {
        this.notifyFail(error);
        return {
          actuallyPeriods: [],
          timeline: { from: [], to: [] },
        };
      };
      return this
        .$appointmentScheduleGateway
        .viewEmployeeTimeLine(employeeId, isoDate, timeStarts)
        .then((result) => result.fold(onFail, (response) => response));
    },
    /**
     * @description Получить рабочие дни сотрудника на месяц.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} yearAndMonth Год и месяц.
     * @returns {Promise<[]>}
     */
    fetchEmployeeWorkDaysByMonth(employeeId, yearAndMonth) {
      const onFail = (error) => {
        this.notifyFail(error.title);
        return [];
      };
      return this
        .$employeeGateway
        .viewWorkDays(employeeId, yearAndMonth)
        .then((result) => result.fold(onFail, (workDays) => workDays));
    },
    /**
     * @description Получить список совпадающих пациентов.
     * @param {GetMatchedPatientsRequest} request
     * @returns {Promise<Array>}
     */
    async fetchMatchedPatients(request) {
      const either = await this.$patientGateway.getMatchedPatients(request);
      return either.fold(
        (error) => {
          this.notifyFail(error.title);
          return [];
        },
        (result) => result,
      );
    },
    /**
     * @description Получить работающих сотрудников.
     * @param {string} isoDate
     * @param {string} period
     * @returns {Promise<Array>}
     */
    fetchWorkEmployees(isoDate) {
      const onResponse = (result) => result.fold(this.appointmentWorkEmployees)((list) => list);
      return this
        .$appointmentScheduleGateway
        .viewWorkingEmployeeList(isoDate, 'day', false)
        .then(onResponse);
    },
    /**
     * @description Получить работающих сотрудников по кабинету.
     * @param {number} cabinetId
     * @param {string} date
     * @param {string} period
     * @returns {Promise<Array>}
     */
    fetchWorkEmployeesByCabinet(cabinetId, date) {
      const onResponse = (result) => result.fold(this.appointmentWorkEmployees)((list) => list);
      return this.$appointmentScheduleGateway
        .viewWorkingEmployeeListByCabinet(cabinetId, date, 'day')
        .then(onResponse);
    },
    /**
     * @description Обработать отчистку списка совпадающих пациентов.
     * @returns {void}
     */
    onClearMatchedPatients() {
      this.appointmentMatchedPatients = [];
    },
    /**
     * @description Обработать закрытие формы записи на прием.
     * @returns {void}
     */
    onCloseAppointmentForm() {
      this.selectedAppointment = Maybe.none();
      this.appointmentEmployee = Maybe.none();
      this.onClearMatchedPatients();
      this.appointmentCabinetId = null;
      this.appointmentInputErrors = EMPTY_INPUT_ERRORS;
    },
    /**
     * @description Обработать получение шкалы времени сотрудника на день.
     * @param {number} employeeId
     * @param {string} isoDate
     * @param {string} timeStarts
     * @returns {void}
     */
    onReloadEmployeeDayTimeline(employeeId, isoDate, timeStarts) {
      this
        .fetchEmployeeDayTimeline(employeeId, isoDate, timeStarts)
        .then(({ timeline }) => {
          this.appointmentEmployeeTimeline = timeline;
        });
    },
    /**
     * @description Обработать изменение даты записи на прием.
     * @param {Date} date
     * @returns {void}
     */
    onInputAppointmentDate(date) {
      this.appointmentDate = date;
      const isoDate = convertToInputDate(date);
      const setEmployeeIfEmpty = () => {
        if (!this.appointmentEmployeeId && this.appointmentWorkEmployees.length === 1) {
          const employeeId = this.appointmentWorkEmployees[0].id;
          this.onInputAppointmentEmployee(employeeId);
          this.onReloadEmployeeDayTimeline(
            employeeId,
            isoDate,
            this.appointmentTimeStarts,
          );
        }
      };
      const onResponse = (employees) => {
        const appointmentPeriod = {
          from: this.appointmentTimeStarts,
          to: this.appointmentTimeEnds,
        };

        let appointmentEmployee = employees.find(
          (candidate) => candidate.id === this.appointmentEmployeeId,
        );

        if (!appointmentEmployee) {
          appointmentEmployee = employees.find(
            (candidate) => candidate.id === this.appointmentEmployee?.id,
          );
        }

        if (!appointmentEmployee) {
          const indexOfAppointmentEmployee = employees.findIndex(
            (el) => el.workPeriod.from <= appointmentPeriod.from
              && el.workPeriod.to >= appointmentPeriod.to,
          );
          this.appointmentEmployeeId = indexOfAppointmentEmployee !== -1
            ? employees[indexOfAppointmentEmployee].id
            : employees[0].id;

          this.onReloadEmployeeDayTimeline(
            this.appointmentEmployeeId,
            isoDate,
            this.appointmentTimeStarts,
          );
        } else {
          this.appointmentEmployeeId = appointmentEmployee.id;
        }
        this.appointmentWorkEmployees = employees;
        setEmployeeIfEmpty();
      };
      if (this.appointmentCabinetId) {
        this
          .fetchWorkEmployeesByCabinet(this.appointmentCabinetId, isoDate)
          .then(onResponse);
      } else {
        this.fetchWorkEmployees(isoDate).then(onResponse);
      }
    },
    /**
     * @description Обработать изменение сотрудника.
     * @param {?number} employeeId
     * @returns {void}
     */
    async onInputAppointmentEmployee(employeeId) {
      this.appointmentEmployeeId = employeeId;
      if (employeeId) {
        this.onInputAppointmentMonth(this.appointmentMonth);
      }
    },
    /**
     * @description Получить список рабочих дней клиники.
     * @param {string} yearAndMonth
     * @returns {Promise<string[]>}
     */
    async fetchClinicWorkDays(yearAndMonth = 'now') {
      const days = await this.$appointmentScheduleGateway.viewClinicWorkDays(true, yearAndMonth);
      return days.fold([])((value) => value);
    },
    /**
     * @description Получить певого пациента в листе ожидания
     * по id пациента
     *
     * @param {int} patientId
     * @returns {Promise<object[]>}
     */
    async fetchFirstPatientsFromWaitingList(patientId) {
      const dateNow = new Date().toISOString().slice(0, 10);
      const waitingList = await this.$waitingListGateway.viewDailyList(dateNow, 'year', patientId);
      return waitingList.fold([])((value) => value.list)[0];
    },
    /**
     * @description Обработать изменение месяца.
     * @param {string} yearAndMonth
     * @returns {void}
     */
    async onInputAppointmentMonth(yearAndMonth) {
      this.appointmentMonth = yearAndMonth;
      if (this.appointmentEmployeeId !== null) {
        this.appointmentEmployeeWorkDayList = await this.fetchClinicWorkDays(
          yearAndMonth,
        );
      }
    },
    /**
     * @description Обработать ввод времени границ записи на прием.
     * @param {string} value
     * @returns {void}
     */
    onInputAppointmentTimes(timeStarts, timeEnds) {
      this.appointmentTimeStarts = timeStarts;
      this.appointmentTimeEnds = timeEnds;
    },
    /**
     * @description Обработать поиск совпадающих пациентов.
     * @param {GetMatchedPatientsRequest} request
     * @returns {void}
     */
    async onMatchedPatients(request) {
      this.appointmentMatchedPatients = await this.fetchMatchedPatients(request);
    },
    /**
     * @description Проверить уведомления.
     *
     * @params  {int}
     * @returns {object}
     */
    async onCheckNotification(patientId) {
      this.waitingPatient = await this.fetchFirstPatientsFromWaitingList(patientId);
    },
    /**
     * @description Удалить данные выбранного пациента.
     * @returns {void}
     */
    unsetPatientParam() {
      this.appointmentPatientId = null;
      this.$route.params.patientId = '';
    },
  },
};
